/**
 * Pure helpers for the multicam annotator: angle names, the program (which
 * angle the creator's cuts show when), the shapes sent to the multicam
 * annotation mutations, and plain-language error copy.
 *
 * The data model is unchanged. An annotation sits on the shared timeline at
 * `timelineTimeMs` (optionally to `endTimeMs`). A SWITCH is a camera cut to
 * `targetAngleKey`; a NOTE is feedback, with `targetAngleKey` naming the angle
 * it is about (null means every angle).
 */

import type { MulticamAngle, MulticamAnnotation, MulticamSet } from '../../graphqlService';
import { DEFAULT_CATEGORY_ID, getAnnotationCategory } from '../../utils/annotationCategories';
import { secondsToFrames } from '../../utils/timecode';
import type { StudioAnnotation } from '../AnnotationStudio/annotationModel';

export type AnnotationKind = 'NOTE' | 'SWITCH';

/** Select value for a note that applies to every angle (stored as null). */
export const ALL_ANGLES = '__ALL__';

/**
 * Category stored on camera cuts. The editor's workspace already shows cuts
 * as this category, and its colour matches the review player's cut colour.
 */
export const CUT_CATEGORY = 'scene';

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

const finite = (value: unknown, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

/** Angles sorted by key, with legacy negative offsets clamped to 0. */
export const normalizeAngles = (angles?: MulticamAngle[] | null): MulticamAngle[] =>
  (Array.isArray(angles) ? angles : [])
    .filter((angle): angle is MulticamAngle => Boolean(angle) && typeof angle.angleKey === 'string' && angle.angleKey !== '')
    .map((angle) => ({
      ...angle,
      label: angle.label ?? '',
      videoUrl: angle.videoUrl ?? '',
      durationMs: finite(angle.durationMs),
      timelineStartMs: Math.max(0, finite(angle.timelineStartMs)),
      sourceInMs: Math.max(0, finite(angle.sourceInMs)),
      sourceOutMs: angle.sourceOutMs === null || angle.sourceOutMs === undefined ? null : finite(angle.sourceOutMs),
    }))
    .sort((a, b) => a.angleKey.localeCompare(b.angleKey));

/** A set the page can render: the API can leave `angles` out on a partial answer. */
export const normalizeMulticamSet = (set: MulticamSet): MulticamSet => ({
  ...set,
  name: set.name || 'Untitled multicam set',
  angles: normalizeAngles(set.angles),
});

export const isCut = (annotation: { type?: string | null }) =>
  String(annotation.type || '').toUpperCase() === 'SWITCH';

/** Time order; annotations at the same moment keep the server's order. */
export const sortAnnotations = <T extends { timelineTimeMs: number }>(list: T[]): T[] =>
  list
    .map((annotation, index) => ({ annotation, index }))
    .sort((a, b) => a.annotation.timelineTimeMs - b.annotation.timelineTimeMs || a.index - b.index)
    .map(({ annotation }) => annotation);

export const normalizeAnnotations = (list?: MulticamAnnotation[] | null): MulticamAnnotation[] =>
  sortAnnotations(
    (Array.isArray(list) ? list : [])
      .filter((annotation): annotation is MulticamAnnotation => Boolean(annotation) && Boolean(annotation.id))
      .map((annotation) => ({
        ...annotation,
        timelineTimeMs: Math.max(0, finite(annotation.timelineTimeMs)),
        endTimeMs: annotation.endTimeMs === null || annotation.endTimeMs === undefined ? null : finite(annotation.endTimeMs),
        type: isCut(annotation) ? 'SWITCH' : 'NOTE',
        text: String(annotation.text ?? ''),
      }))
  );

// ---------------------------------------------------------------------------
// Angles
// ---------------------------------------------------------------------------

/** The master angle: what the program shows before the first cut. */
export const masterAngleKey = (angles: Array<{ angleKey: string }>): string =>
  angles.find((angle) => angle.angleKey === 'A')?.angleKey ?? angles[0]?.angleKey ?? 'A';

/** The angle's own label, or '' when it only repeats "Angle B". */
export const angleLabel = (angle: { angleKey: string; label?: string | null }): string => {
  const label = String(angle.label ?? '').trim();
  return label && label.toLowerCase() !== `angle ${angle.angleKey}`.toLowerCase() ? label : '';
};

/** "B · Close-up", or "Angle B" for an angle without a label of its own. */
export const angleName = (angle: { angleKey: string; label?: string | null }): string => {
  const label = angleLabel(angle);
  return label ? `${angle.angleKey} · ${label}` : `Angle ${angle.angleKey}`;
};

/** For screen readers, which read "·" aloud: "angle B, Close-up". */
export const angleSpokenName = (angle: { angleKey: string; label?: string | null }): string => {
  const label = angleLabel(angle);
  return label ? `angle ${angle.angleKey}, ${label}` : `angle ${angle.angleKey}`;
};

export const findAngle = <T extends { angleKey: string }>(angles: T[], key?: string | null): T | undefined =>
  key ? angles.find((angle) => angle.angleKey === key) : undefined;

/** "Cut to B · Close-up" (or "Cut to angle D" when that angle is gone). */
export const cutLabel = (targetAngleKey: string | null | undefined, angles: Array<{ angleKey: string; label?: string | null }>) => {
  const target = findAngle(angles, targetAngleKey);
  return target ? `Cut to ${angleName(target)}` : `Cut to angle ${targetAngleKey || '?'}`;
};

// ---------------------------------------------------------------------------
// The program: which angle the cuts show when
// ---------------------------------------------------------------------------

/** Camera cuts in time order (a later cut at the same moment wins). */
export const sortedCuts = (annotations: MulticamAnnotation[]): MulticamAnnotation[] =>
  sortAnnotations(annotations.filter((annotation) => isCut(annotation) && Boolean(annotation.targetAngleKey)));

/**
 * The angle the cut shows at `seconds`. Compared by frame, so a cut is live
 * from the first frame it is on. Before the first cut it is the master.
 */
export const liveAngleKeyAt = (
  annotations: MulticamAnnotation[],
  seconds: number,
  masterKey: string,
  fps: number
): string => {
  const frame = secondsToFrames(seconds, fps);
  let live = masterKey;
  for (const cut of sortedCuts(annotations)) {
    if (secondsToFrames(cut.timelineTimeMs / 1000, fps) > frame) break;
    live = cut.targetAngleKey as string;
  }
  return live;
};

/** A cut already on the frame at `seconds`, if any. */
export const cutAtFrame = (annotations: MulticamAnnotation[], seconds: number, fps: number): MulticamAnnotation | null => {
  const frame = secondsToFrames(seconds, fps);
  return sortedCuts(annotations).find((cut) => secondsToFrames(cut.timelineTimeMs / 1000, fps) === frame) ?? null;
};

export interface ProgramSegment {
  id: string;
  /** The cut that starts this stretch; null for the master before the first cut. */
  cutId: string | null;
  angleKey: string;
  /** Seconds. */
  start: number;
  end: number;
}

/** The program as stretches of one angle each, from 0 to `durationS`. */
export const programSegments = (
  annotations: MulticamAnnotation[],
  masterKey: string,
  durationS: number
): ProgramSegment[] => {
  if (!(durationS > 0)) return [];
  const segments: ProgramSegment[] = [];
  let start = 0;
  let angleKey = masterKey;
  let cutId: string | null = null;
  for (const cut of sortedCuts(annotations)) {
    const at = Math.min(durationS, Math.max(0, cut.timelineTimeMs / 1000));
    if (at > start) segments.push({ id: `segment:${cutId ?? 'start'}`, cutId, angleKey, start, end: at });
    start = Math.max(start, at);
    angleKey = cut.targetAngleKey as string;
    cutId = cut.id;
  }
  if (durationS > start) segments.push({ id: `segment:${cutId ?? 'start'}`, cutId, angleKey, start, end: durationS });
  return segments;
};

/**
 * Which angle a new cut should go to: the angle on screen, unless the cut
 * already shows it here; then the next angle that has footage.
 */
export const defaultCutTarget = (
  angleKeys: string[],
  viewKey: string,
  liveKey: string,
  hasFootage: (angleKey: string) => boolean
): string | null => {
  if (viewKey !== liveKey && angleKeys.includes(viewKey) && hasFootage(viewKey)) return viewKey;
  const from = Math.max(0, angleKeys.indexOf(viewKey));
  for (let step = 1; step <= angleKeys.length; step += 1) {
    const key = angleKeys[(from + step) % angleKeys.length];
    if (key !== liveKey && hasFootage(key)) return key;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Display in the studio's notes list and timeline
// ---------------------------------------------------------------------------

const rangeEndSeconds = (annotation: MulticamAnnotation): number | undefined =>
  typeof annotation.endTimeMs === 'number' && annotation.endTimeMs > annotation.timelineTimeMs
    ? annotation.endTimeMs / 1000
    : undefined;

/** An annotation in the studio's shape (seconds, category, text to show). */
export const toPanelAnnotation = (
  annotation: MulticamAnnotation,
  angles: Array<{ angleKey: string; label?: string | null }>
): StudioAnnotation => {
  const endTime = rangeEndSeconds(annotation);
  const base = { id: annotation.id, time: annotation.timelineTimeMs / 1000, ...(endTime !== undefined ? { endTime } : {}) };
  if (isCut(annotation)) {
    const reason = annotation.text.trim();
    return {
      ...base,
      text: `${cutLabel(annotation.targetAngleKey, angles)}${reason ? ` — ${reason}` : ''}`,
      category: CUT_CATEGORY,
      color: getAnnotationCategory(CUT_CATEGORY).color,
    };
  }
  return {
    ...base,
    text: annotation.text,
    category: annotation.category || DEFAULT_CATEGORY_ID,
    color: annotation.color || undefined,
  };
};

// ---------------------------------------------------------------------------
// Saving
// ---------------------------------------------------------------------------

/**
 * Seconds -> milliseconds for the API (a Float). Rounded to the nanosecond
 * only to drop float noise (5033.333000000001): coarser rounding could push a
 * time that sits exactly on a frame boundary into the frame before it.
 */
export const msFromSeconds = (seconds: number): number =>
  Math.round(Math.max(0, Number.isFinite(seconds) ? seconds : 0) * 1e9) / 1e6;

export interface AnnotationDraft {
  type: AnnotationKind;
  /** Seconds on the shared timeline. */
  time: number;
  endTime?: number | null;
  text: string;
  category?: string | null;
  /** NOTE: the angle it is about (null: every angle). SWITCH: the angle cut to. */
  targetAngleKey: string | null;
  /** Colour stored with a category outside the shared list, kept as is. */
  storedColor?: string | null;
}

export interface MulticamAnnotationWrite {
  timelineTimeMs: number;
  endTimeMs: number | null;
  type: AnnotationKind;
  targetAngleKey: string | null;
  text: string;
  category: string;
  color: string;
}

/**
 * Every field the multicam annotation mutations take. Cuts are stored with
 * the cut category; notes with the shared category colour (so a Dialogue note
 * is the same blue in the studio, the review player and the EDL export).
 */
export const toMutationInput = (draft: AnnotationDraft): MulticamAnnotationWrite => {
  const category = draft.type === 'SWITCH' ? CUT_CATEGORY : (draft.category || DEFAULT_CATEGORY_ID);
  const end = typeof draft.endTime === 'number' && Number.isFinite(draft.endTime) && draft.endTime > draft.time
    ? draft.endTime
    : null;
  return {
    timelineTimeMs: msFromSeconds(draft.time),
    endTimeMs: end === null ? null : msFromSeconds(end),
    type: draft.type,
    targetAngleKey: draft.targetAngleKey,
    text: draft.text,
    category,
    color: getAnnotationCategory(category, draft.storedColor).color,
  };
};

/** The draft an existing annotation would save as unchanged. */
export const draftFromAnnotation = (annotation: MulticamAnnotation): AnnotationDraft => ({
  type: isCut(annotation) ? 'SWITCH' : 'NOTE',
  time: annotation.timelineTimeMs / 1000,
  endTime: rangeEndSeconds(annotation) ?? null,
  text: annotation.text,
  category: annotation.category,
  targetAngleKey: annotation.targetAngleKey,
  storedColor: annotation.color,
});

// ---------------------------------------------------------------------------
// Errors, in words people can act on
// ---------------------------------------------------------------------------

const messageOf = (error: unknown): string => {
  if (error instanceof Error) return error.message || '';
  return typeof error === 'string' ? error : '';
};

export type LoadProblem = 'not-found' | 'no-access' | 'offline' | 'unknown';

export const classifyLoadError = (error: unknown): LoadProblem => {
  const message = messageOf(error).toLowerCase();
  if (/not found|does not exist|no longer exists/.test(message)) return 'not-found';
  if (/permission|not allowed|forbidden|unauthori[sz]ed|access denied/.test(message)) return 'no-access';
  if (/network|failed to fetch|load failed|timed? ?out|offline|status code 5\d\d/.test(message)) return 'offline';
  return 'unknown';
};

export const LOAD_ERROR_COPY: Record<LoadProblem, { title: string; body: string }> = {
  'not-found': {
    title: 'We couldn’t find this multicam set',
    body: 'It may have been deleted, or the link may be out of date.',
  },
  'no-access': {
    title: 'You don’t have access to this multicam set',
    body: 'It belongs to another account. Check that you’re signed in to the right one.',
  },
  offline: {
    title: 'We couldn’t load this multicam set',
    body: 'Check your connection, then try again.',
  },
  unknown: {
    title: 'We couldn’t load this multicam set',
    body: 'Something went wrong on our side. Try again in a moment.',
  },
};

export type SaveAction = 'add-note' | 'add-cut' | 'update' | 'move' | 'delete' | 'status';

const SAVE_ACTION_COPY: Record<SaveAction, string> = {
  'add-note': 'Couldn’t add the note.',
  'add-cut': 'Couldn’t add the cut.',
  update: 'Couldn’t save your changes.',
  move: 'Couldn’t move it.',
  delete: 'Couldn’t delete it.',
  status: 'Couldn’t mark the set as ready.',
};

export const describeSaveError = (error: unknown, action: SaveAction): string => {
  const message = messageOf(error).toLowerCase();
  let reason = 'Try again in a moment.';
  if (/locked/.test(message)) reason = 'This set was submitted for editing, so it can’t be changed.';
  else if (/permission|forbidden|not allowed/.test(message)) reason = 'You don’t have permission to change this set.';
  else if (/sign in|session|authenticat|token/.test(message)) reason = 'Your session expired. Sign in again, then retry.';
  else if (/network|failed to fetch|load failed|timed? ?out|offline/.test(message)) reason = 'Check your connection, then try again.';
  else if (/not found/.test(message)) reason = 'It was changed somewhere else. Reload the page to see the latest version.';
  return `${SAVE_ACTION_COPY[action]} ${reason}`;
};
